/**
 * Route visual signatures (Neon-Noir flightcase trims).
 *
 * - ROUTE_SIGNATURE: one dominant accent per main route (player flows).
 * - SECTION_ACCENT: semantic overrides (danger, success, live) — use sparingly.
 *
 * Admin uses SECTION_ACCENT for multi-tool blocks; player routes use one signature + neutrals.
 */

export const ROUTE_SIGNATURE = {
  dashboard: {
    heroCard:
      'border-neon-cyan border-2 shadow-[0_0_20px_rgba(0,173,239,0.18)]',
    mutedCard: 'border-neon-cyan/50 border-2',
    stickyBar: 'border-b border-neon-cyan/60',
    tabActive:
      'bg-neon-cyan/20 text-neon-cyan border-2 border-neon-cyan border-b-0 -mb-0.5',
    quickActionBtn:
      'bg-neon-cyan/10 hover:bg-neon-cyan/20 border border-neon-cyan/20 hover:border-neon-cyan/40',
    holeCardHover: 'hover:border-neon-cyan',
    /**
     * Primary focus — the LIVE hole on your route. Also carries the team
     * score / par / progress chips that used to live in a separate hero card,
     * so it deserves stronger glow than other in-list cards.
     */
    nextHoleCard:
      'border-neon-cyan border-2 shadow-[0_0_28px_rgba(0,173,239,0.35)] ring-2 ring-neon-cyan/35',
    /** Completed / recorded — quiet; not platinum (avoid "alert" gold). */
    completedHoleCard: 'border-gray-700 border-2 opacity-95',
  },

  /** Scoring / Play — same cyan family as dashboard (active round). */
  play: {
    heroCard:
      'border-neon-cyan border-2 shadow-[0_0_16px_rgba(0,173,239,0.14)]',
    mutedCard: 'border-neon-cyan/50 border-2',
    playerExpanded: 'border-neon-cyan glow-cyan bg-panel',
  },

  /** Standings — platinum (podium / vinyl). */
  leaderboard: {
    heroCard:
      'border-platinum/60 border-2 shadow-[0_0_18px_rgba(212,175,55,0.12)]',
    mutedCard: 'border-platinum/45 border-2',
    ticker: 'border-platinum/50 border-2',
    rowHover: 'hover:border-platinum/55',
  },

  /** Voting — fuchsia (social / competition). */
  voting: {
    heroCard:
      'border-neon-fuchsia border-2 shadow-[0_0_18px_rgba(230,30,140,0.14)]',
    mutedCard: 'border-neon-fuchsia/55 border-2',
    tabActive:
      'bg-neon-fuchsia/20 text-neon-fuchsia border-2 border-neon-fuchsia border-b-0 -mb-0.5',
    tabActiveAlt:
      'bg-platinum/15 text-platinum border-2 border-platinum border-b-0 -mb-0.5',
  },

  /** Raffle — orange (prize / encore energy). */
  raffle: {
    heroCard:
      'border-psych-orange/70 border-2 shadow-[0_0_18px_rgba(247,148,29,0.18)]',
    mutedCard: 'border-psych-orange/45 border-2',
    filterSelected:
      'bg-psych-orange/20 border-psych-orange text-psych-orange',
    /**
     * Lineup tiers — keyed by `accent` from `src/lib/raffleTiers.js`.
     * Each tier carries the four classes the billing UI needs to stay
     * visually consistent: a `border` (card edge), `glow` (drop shadow),
     * `text` (tier label / type color) and `chip` (sticker plate trim).
     *
     * Glow strings include the literal `shadow-[...]` arbitrary value so
     * Tailwind's JIT picks them up at build time — keep them as full
     * class strings, NOT computed at runtime.
     */
    tiers: {
      orange: {
        border: 'border-psych-orange/70',
        glow: 'shadow-[0_0_28px_rgba(247,148,29,0.45)]',
        text: 'text-psych-orange',
        chip: 'border-psych-orange/70 bg-psych-orange/15 text-psych-orange',
        gradient:
          'linear-gradient(135deg, rgba(247,148,29,0.22) 0%, rgba(230,30,140,0.18) 60%, rgba(91,45,120,0.12) 100%)',
      },
      purple: {
        border: 'border-psych-purple/70',
        glow: 'shadow-[0_0_22px_rgba(145,70,255,0.40)]',
        text: 'text-psych-purple',
        chip: 'border-psych-purple/70 bg-psych-purple/20 text-white',
        gradient:
          'linear-gradient(135deg, rgba(91,45,120,0.28) 0%, rgba(145,70,255,0.18) 60%, rgba(0,0,0,0.10) 100%)',
      },
      cyan: {
        border: 'border-neon-cyan/70',
        glow: 'shadow-[0_0_18px_rgba(0,173,239,0.35)]',
        text: 'text-neon-cyan',
        chip: 'border-neon-cyan/70 bg-neon-cyan/15 text-neon-cyan',
        gradient:
          'linear-gradient(135deg, rgba(0,173,239,0.18) 0%, rgba(0,173,239,0.08) 60%, rgba(0,0,0,0.10) 100%)',
      },
      lime: {
        border: 'border-neon-lime/70',
        glow: 'shadow-[0_0_16px_rgba(200,230,50,0.30)]',
        text: 'text-neon-lime',
        chip: 'border-neon-lime/70 bg-neon-lime/15 text-neon-lime',
        gradient:
          'linear-gradient(135deg, rgba(200,230,50,0.18) 0%, rgba(200,230,50,0.06) 60%, rgba(0,0,0,0.10) 100%)',
      },
    },
  },

  /** Login — subtle purple frame (entry gate), paper panel stays light. */
  login: {
    panel: 'border-psych-purple/40',
  },
}

/** Semantic trims — not route-specific; pair with flightcase `rivets` where needed. */
export const SECTION_ACCENT = {
  danger: 'border-grunge-red',
  dangerMuted: 'border-grunge-red/60',
  success: 'border-grunge-green',
  live: 'border-neon-cyan',
  liveMuted: 'border-neon-cyan/60',
  data: 'border-grunge-green',
  social: 'border-neon-fuchsia',
}
